import { Link } from "react-router-dom";

const COL_W = 240;
const ROW_H = 28;
const PAD_X = 16;
const PAD_Y = 16;
const LABEL_W = 220;

const isFemale = (g) => g === "Гүү" || g === "Үрээ";

// Mutates node to add x, y. Returns total leaves count.
const layoutTree = (node, depth = 0, counter = { v: 0 }) => {
  node._x = depth;
  if (!node.children || node.children.length === 0) {
    node._y = counter.v;
    counter.v += 1;
    return;
  }
  for (const c of node.children) layoutTree(c, depth + 1, counter);
  const ys = node.children.map((c) => c._y);
  node._y = (Math.min(...ys) + Math.max(...ys)) / 2;
};

const collect = (node, nodes = [], links = []) => {
  nodes.push(node);
  if (node.children) {
    for (const c of node.children) {
      links.push({ from: node, to: c });
      collect(c, nodes, links);
    }
  }
  return { nodes, links };
};

const GenderTag = ({ gender }) => {
  const fem = isFemale(gender);
  return (
    <span className={`font-bold mr-1 ${fem ? "text-pink-500" : "text-blue-500"}`}>
      {fem ? "♀" : "♂"}
    </span>
  );
};

const ProgenyTree = ({ tree }) => {
  if (!tree) return null;
  // Deep clone to avoid mutating prop
  const root = JSON.parse(JSON.stringify(tree));
  const counter = { v: 0 };
  layoutTree(root, 0, counter);
  const totalLeaves = Math.max(counter.v, 1);
  const { nodes, links } = collect(root);
  const maxDepth = Math.max(...nodes.map((n) => n._x));
  const width = (maxDepth + 1) * COL_W + LABEL_W + PAD_X * 2;
  const height = totalLeaves * ROW_H + PAD_Y * 2;

  // Header text
  const fem = isFemale(tree.gender);
  const kind = fem ? "гүүний" : "азарганы";

  return (
    <div className="w-full">
      <div
        className="mb-3 text-sm text-[#2C241B] border border-[#D6CFC4] bg-[#F0EBE1]/40 rounded p-3"
        data-testid="progeny-header"
      >
        <GenderTag gender={tree.gender} />
        <span className="font-bold">{tree.name}</span>
        {tree.birth_year ? ` ${tree.birth_year}` : ""} {kind}{" "}
        <span className="font-bold">{tree.depth || 3}</span> үед нийт үр төлийн{" "}
        <span className="font-bold text-[#1B3B5A]">{tree.total_descendants}</span> адуу бүртгэгдсэн байна.
      </div>

      <div
        className="w-full overflow-x-auto scroll-soft border border-[#D6CFC4] rounded bg-white"
        data-testid="progeny-tree"
      >
        <div style={{ position: "relative", width, height }}>
          <svg
            width={width}
            height={height}
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
          >
            {links.map((l, idx) => {
              const x1 = l.from._x * COL_W + PAD_X + LABEL_W * 0.9;
              const y1 = l.from._y * ROW_H + PAD_Y + ROW_H / 2;
              const x2 = l.to._x * COL_W + PAD_X;
              const y2 = l.to._y * ROW_H + PAD_Y + ROW_H / 2;
              const mx = (x1 + x2) / 2;
              return (
                <path
                  key={`l-${idx}`}
                  d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
                  stroke="#67c39a"
                  strokeWidth={1.1}
                  fill="none"
                  opacity={0.75}
                />
              );
            })}
          </svg>
          {nodes.map((n) => {
            const left = n._x * COL_W + PAD_X;
            const top = n._y * ROW_H + PAD_Y;
            const fem2 = isFemale(n.gender);
            return (
              <Link
                key={n.id}
                to={`/horse/${n.id}`}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width: LABEL_W,
                  height: ROW_H,
                  display: "flex",
                  alignItems: "center",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  overflow: "hidden",
                }}
                className={fem2 ? "text-pink-600 hover:text-pink-800" : "text-blue-600 hover:text-blue-800"}
                title={n.name}
                data-testid={`progeny-node-${n.id}`}
              >
                <span className="font-bold mr-1">{fem2 ? "♀" : "♂"}</span>
                <span className="truncate">
                  {n.name}
                  {n.birth_year ? ` ${n.birth_year}` : ""}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgenyTree;
